import { Component, computed, inject, input, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { ButtonComponent } from '../../../controls/components/basic/button/button.component';
import { ButtonPrimaryDirective } from '../../../controls/directives/button-primary.directive';
import { ButtonSecondaryDirective } from '../../../controls/directives/button-secondary.directive';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ButtonSubmitDirective } from '../../../controls/directives/button-submit.directive';
import { DatetimeboxComponent } from '../../../controls/components/basic/datetimebox/datetimebox.component';
import { LayoutRowComponent } from '../../../controls/components/basic/layout-row/layout-row.component';
import { LayoutColumnComponent } from '../../../controls/components/basic/layout-column/layout-column.component';
import { FormsModule, NgForm } from '@angular/forms';
import { CalculateValidationTextFn } from '../../../controls/components/internal/control-supporting-text/control-supporting-text.component';
import { DateRangeValidatorDirective } from '../../../controls/directives/date-range-validator.directive';
import { FutureDateValidatorDirective } from '../../../controls/directives/future-date-validator.directive';
import { TextboxComponent } from 'src/app/controls/components/basic/textbox/textbox.component';
import { AbsenceConfigurationPresentation } from '../../models/absence-configuration-presentation';

@Component({
  selector: 'app-alert-absence-dialog',
  templateUrl: './alert-absence-dialog.component.html',
  styleUrl: './alert-absence-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [FormsModule, DateRangeValidatorDirective, FutureDateValidatorDirective, ButtonComponent, ButtonPrimaryDirective, ButtonSecondaryDirective, ButtonSubmitDirective, DatetimeboxComponent, LayoutRowComponent, LayoutColumnComponent, TextboxComponent]
})
export class AlertAbsenceDialogComponent {
  private readonly dialog = inject(NgbActiveModal);

  readonly absenceStart = input<Date>(null);
  readonly absenceEnd = input<Date>(null);
  readonly absenceNote = input<string>(null);

  readonly isAbsenceScheduled = computed(() => !!this.absenceEnd() && new Date() <= this.absenceEnd());

  readonly getStartDateValidationText: CalculateValidationTextFn = (control, defaultValidationText) =>
    control?.errors?.['startBeforeEnd']
      ? 'Start date must be before end date.'
      : defaultValidationText;

  readonly getEndDateValidationText: CalculateValidationTextFn = (control, defaultValidationText) =>
    control?.errors?.['startBeforeEnd']
      ? 'End date must be after start date.'
      : control?.errors?.['futureDate']
        ? 'End date must be in the future.'
        : defaultValidationText;

  private readonly form = viewChild<NgForm>('absenceForm');

  setAbsense(form: NgForm): void {
    const absence: AbsenceConfigurationPresentation = {
      FromDateTime: this.getDateValue(form.value, 'startDate'),
      ToDateTime: this.getDateValue(form.value, 'endDate'),
      Note: this.getStringValue(form.value, 'note')
    };
    this.dialog.close(absence);
  }

  cancelDialog(): void {
    this.dialog.dismiss();
  }

  cancelAbsence(): void {
    this.dialog.close(null);
  }

  private getDateValue(formValue: unknown, key: string): Date | null {
    if (!formValue || typeof formValue !== 'object') {
      return null;
    }
    const value = (formValue as Record<string, unknown>)[key];
    return value instanceof Date ? value : null;
  }

  private getStringValue(formValue: unknown, key: string): string {
    if (!formValue || typeof formValue !== 'object') {
      return '';
    }
    const value = (formValue as Record<string, unknown>)[key];
    return typeof value === 'string' ? value : '';
  }
}
